import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import {
  inventoryApi,
  inventoryQueryKeys,
  type StockRow,
} from "../../shared/api/inventory";
import { formatDate } from "../../shared/format";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/**
 * T71 — Tồn kho. Hiển thị số lượng thuốc còn lại theo lô (FEFO) cùng cảnh
 * báo sắp hết hạn / sắp hết hàng. Dữ liệu lấy từ {@code /v1/dispensing/stock}.
 */
const LOW_STOCK_THRESHOLD = 10; // viên — chuẩn cho HIS Lite (cấu hình sau)
const EXPIRING_DAYS = 60;

export function StockPage() {
  const [q, setQ] = useState("");
  const [onlyAlerts, setOnlyAlerts] = useState(false);

  const query = useQuery({
    queryKey: inventoryQueryKeys.stock(),
    queryFn: () => inventoryApi.stock(),
  });

  const rows = useMemo(() => {
    const all = query.data ?? [];
    const today = new Date();
    const expiringCutoff = new Date(today);
    expiringCutoff.setDate(today.getDate() + EXPIRING_DAYS);
    const needle = q.trim().toLowerCase();
    return all.filter((r) => {
      if (needle && !r.medicineName.toLowerCase().includes(needle))
        return false;
      if (onlyAlerts) {
        const exp = r.expiryDate ? new Date(r.expiryDate) : null;
        const expiring = exp ? exp <= expiringCutoff : false;
        const low = (r.quantityOnHand ?? 0) <= LOW_STOCK_THRESHOLD;
        if (!expiring && !low) return false;
      }
      return true;
    });
  }, [query.data, q, onlyAlerts]);

  function badgeFor(r: StockRow): JSX.Element | null {
    const today = new Date();
    const cutoff = new Date(today);
    cutoff.setDate(today.getDate() + EXPIRING_DAYS);
    const exp = r.expiryDate ? new Date(r.expiryDate) : null;
    if (exp && exp < today)
      return <Badge variant="destructive">Đã hết hạn</Badge>;
    if (exp && exp <= cutoff)
      return <Badge variant="warning">Sắp hết hạn</Badge>;
    if ((r.quantityOnHand ?? 0) <= 0)
      return <Badge variant="destructive">Hết hàng</Badge>;
    if ((r.quantityOnHand ?? 0) <= LOW_STOCK_THRESHOLD)
      return <Badge variant="warning">Tồn kho thấp</Badge>;
    return <Badge variant="success">Còn hàng</Badge>;
  }

  return (
    <div>
      <PageHeader
        title="Tồn kho thuốc"
        description="Số lượng tồn kho theo lô (FEFO — hết hạn trước, cấp phát trước)."
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/settings/medicines">Danh mục thuốc</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/inventory/adjustments">Điều chỉnh tồn kho</Link>
            </Button>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Tìm theo tên thuốc…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={onlyAlerts}
            onCheckedChange={(v) => setOnlyAlerts(v === true)}
          />
          Chỉ hiển thị mục cần chú ý
        </label>
      </div>

      {query.isError && (
        <p className="mb-4 text-sm text-destructive">
          Không tải được dữ liệu tồn kho.
        </p>
      )}

      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tên thuốc</TableHead>
              <TableHead>Lô</TableHead>
              <TableHead>Hạn dùng</TableHead>
              <TableHead className="text-right">Tồn kho</TableHead>
              <TableHead>Tình trạng</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  Đang tải…
                </TableCell>
              </TableRow>
            )}
            {!query.isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  Chưa có dữ liệu tồn kho. Nhập kho bằng trang{" "}
                  <Link
                    to="/inventory/adjustments"
                    className="text-primary hover:underline"
                  >
                    Điều chỉnh tồn kho
                  </Link>
                  .
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={`${r.medicineId}-${r.batchId}`}>
                <TableCell>{r.medicineName}</TableCell>
                <TableCell className="font-mono tabular-nums">{r.lotNumber}</TableCell>
                <TableCell>{formatDate(r.expiryDate)}</TableCell>
                <TableCell className="text-right tabular-nums">{r.quantityOnHand}</TableCell>
                <TableCell>{badgeFor(r)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
